import { Spin } from 'antd'
import React, { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import Blur from '@/components/common/blur'
import KolDetailStatistic from '@/components/kol/common/kol-detail-statistic'
import AudienceAgeGenderChart from '@/components/kol/detail/audience-analyses/audience-age-gender-chart'
import {
  Percentage,
  StatisticWrapper,
} from '@/components/kol/detail/audience-analyses/audience-analysis'
import Blocker from '@/components/kol/detail/blocker'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Audience } from '@/store/kol-detail'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { Platform } from '@/utils/convert-platform'

const FAKE_MAIN_GENDER = 0.76

interface AudienceAgeGenderProps {
  audienceData?: Audience
  kolInfo?: KolSchemaModel
  isFetchingKolInfo: boolean
  isFetchingAudienceAnalyze: boolean
  isLocked: boolean
  showMockData?: boolean
  isPublicDetailPage: boolean
  platform: Platform
  enabledLocationData: boolean
}

const Statistics: FunctionComponent<AudienceAgeGenderProps> = ({
  audienceData,
  kolInfo,
  isFetchingKolInfo,
  isLocked,
  showMockData,
  platform,
}) => {
  const { formatMessage, formatNumber } = useIntl()

  const kpi = kolInfo?.kpi.find((kpi) => kpi.platform === platform.shortcode)

  const activeBlur = isLocked && !showMockData

  return (
    <StatisticWrapper>
      <KolDetailStatistic
        loading={isFetchingKolInfo}
        suffix={((): ReactNode | undefined => {
          if (activeBlur) {
            return (
              <Percentage>
                <Blur activate>
                  {formatNumber(FAKE_MAIN_GENDER, {
                    isPercentageDisplay: true,
                  })}
                </Blur>
              </Percentage>
            )
          }

          return kpi?.audience?.mainGender?.weight ? (
            <Percentage>
              {formatNumber(kpi.audience.mainGender.weight, {
                isPercentageDisplay: true,
              })}
            </Percentage>
          ) : undefined
        })()}
        title={formatMessage({ id: 'kol:audience_analysis_gender' })}
        value={
          kpi?.audience?.mainGender?.code
            ? formatMessage({
                id:
                  kpi.audience.mainGender.code === 'male'
                    ? 'kol:audience_analysis_gender_male'
                    : 'kol:audience_analysis_gender_female',
              })
            : 'noData'
        }
      />
      <KolDetailStatistic
        loading={isFetchingKolInfo}
        suffix={((): ReactNode | undefined => {
          if (audienceData?.mainAge?.weight === undefined) {
            return undefined
          }

          return (
            <Percentage>
              <Blur activate={activeBlur}>
                {formatNumber(audienceData.mainAge.weight, {
                  isPercentageDisplay: true,
                })}
              </Blur>
            </Percentage>
          )
        })()}
        title={formatMessage({ id: 'kol:audience_main_age' })}
        value={
          audienceData?.mainAge?.code ? (
            <Blur activate={activeBlur}>
              {formatMessage(
                { id: 'kol:audience_analysis_age_range' },
                { ageRange: audienceData.mainAge.code },
              )}
            </Blur>
          ) : (
            'noData'
          )
        }
      />
    </StatisticWrapper>
  )
}

const Chart: FunctionComponent<AudienceAgeGenderProps> = ({
  isLocked,
  showMockData,
  isPublicDetailPage,
  audienceData,
  isFetchingAudienceAnalyze,
}) => {
  const activeBlur = isLocked && !showMockData

  return (
    <Spin indicator={<Blocker />} spinning={isPublicDetailPage}>
      <AudienceAgeGenderChart
        audienceData={audienceData}
        blur={activeBlur}
        isFetching={isFetchingAudienceAnalyze}
      />
    </Spin>
  )
}

const AudienceAgeGender: FunctionComponent<AudienceAgeGenderProps> = (
  props,
) => {
  const { enabledLocationData } = props

  if (enabledLocationData) {
    return (
      <Box>
        <Statistics {...props} />
        <Chart {...props} />
      </Box>
    )
  }

  return (
    <>
      <Statistics {...props} />
      <Box style={{ border: 'none' }}>
        <Chart {...props} />
      </Box>
    </>
  )
}

export default AudienceAgeGender

const Box = styled.div`
  display: grid;
  gap: 30px;
  border-right: 1px solid ${({ theme }): string => theme.colors.base.grey.grey6};

  @media (max-width: 980px) {
    border-right: none;
  }
`
